import { memo, useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { isEmpty, isNil } from 'lodash';
import { isNode } from 'reactflow';
// Import Types
import type { VisualizationIcon, WorkflowVisualization } from 'types';
// Import Store
import { useAppDispatch, useAppSelector } from 'store';
import { selectPartnerLanguages } from 'store/slices/partnerLanguages';
// Import UI Components
import { BusyLoader, Tag, Tabs, Tab } from '@geneui/components';
// Import Components
import KeyValueItems, { KeyValueItemsProps } from 'components/common/KeyValueItems';
import TittleLink from 'components/common/TittleLink';
import CustomerJourneyVisualizationStepFeaturesInfo from 'components/CustomerJourneyVisualization/CustomerJourneyVisualizationStepFeaturesInfo';
import CustomerJourneyVisualizationLanguages from 'components/CustomerJourneyVisualization/CustomerJourneyVisualizationLanguages';
import CustomerJourneyVisualizationIcon from 'components/CustomerJourneyVisualization/CustomerJourneyVisualizationIcon';
import { useVersionHistoryContext } from 'components/VersionHistory';
// Import Helpers
import { customMomentWithoutTimezoneConversion, getPartnerLanguageList, ignoreCanceledError } from 'services/Helpers';
import {
    getCJVisualizationDueDateInfo,
    getModalCollectedProperties,
    getOutgoers,
    groupBlocksBuilder,
    isCJReferenceInArgumentValue,
    mapConfigToFlowEditorModel,
    parseCJVisualizationDueDateValue,
    propsCollector,
} from 'services/customerJourney/helpers';
// Import Services
import { CustomerJourneyHttpService, UtilsHttpService } from 'services/http';
// Import Hooks
import { usePartnerDateFormats } from 'hooks/UsePartnerDateFormats';
import useRequest from 'hooks/UseRequest';

import useCancelableRequests from 'hooks/UseCancelableRequests';
// Import Constants
import {
    BooleanTypeLabels,
    StateModesLabels,
    JSON_OBJECT,
    l,
    CJModelRelationScopes,
    CJVisualizationDueDateTypeLabels,
    RealtimePromotionTypes,
} from 'constants/common';

const CJVisualizationInfo = ({ workflowId }) => {
    const { t } = useTranslation();

    const { doGetRequest } = useRequest();
    const { partnerDateTimeFormat } = usePartnerDateFormats();
    const { pointInTime } = useVersionHistoryContext();

    const [isLoading, setIsLoading] = useState(false);
    const [visualizationData, setVisualizationData] = useState<Nullable<WorkflowVisualization>>(null);
    const [visualizationIcons, setVisualizationIcons] = useState<Nullable<VisualizationIcon[]>>(null);
    const [subTubIndex, setSubTubIndex] = useState('0');
    const [configBlocks, setConfigBlocks] = useState<AnyObject[]>([]);
    const [schemaGroupBlocks, setSchemaGroupBlocks] = useState<AnyObject[]>([]);
    const [isSchemaLoading, setIsSchemaLoading] = useState(false);

    const dispatch = useAppDispatch();
    const partnerLanguages = useAppSelector(selectPartnerLanguages);

    const {
        getCustomerJourneyVisualizationByIdRequest,
        getVisualizationIconsRequest,
        getPartnerLanguageRequest,
        getCustomerJourneyPreviewByIdRequest,
        getCustomerJourneySchemaRequest,
    } = useCancelableRequests(
        {
            getCustomerJourneyVisualizationByIdRequest: CustomerJourneyHttpService.getCustomerJourneyVisualizationById,
            getVisualizationIconsRequest: CustomerJourneyHttpService.getVisualizationIcons,
            getPartnerLanguageRequest: UtilsHttpService.getPartnerLanguage,
            getCustomerJourneyPreviewByIdRequest: CustomerJourneyHttpService.getCustomerJourneyPreviewById,
            getCustomerJourneySchemaRequest: CustomerJourneyHttpService.getCustomerJourneySchema,
        },
        { takeLatest: ['getCustomerJourneyVisualizationByIdRequest', 'getVisualizationIconsRequest'] },
    );

    const blockNames = useMemo(
        (): Record<string, string> =>
            configBlocks.reduce((acc, block) => {
                acc[block.Name] = block.DisplayName ?? block.Name;
                return acc;
            }, {}),
        [configBlocks],
    );

    const hasDueDateReference = useMemo(() => {
        const dueDates = [
            visualizationData?.DueDate,
            ...(visualizationData?.VisualizationSteps ?? []).flatMap((step) => [
                step.DueDate,
                ...step.VisualizationSubSteps.map((subStep) => subStep.DueDate),
            ]),
        ];
        return dueDates.some((dueDate) => isCJReferenceInArgumentValue(parseCJVisualizationDueDateValue(dueDate)));
    }, [visualizationData]);

    const referenceProperties = useMemo((): Nullable<AnyObject[]> => {
        if (isEmpty(schemaGroupBlocks) || isEmpty(configBlocks)) {
            return null;
        }
        const elements = mapConfigToFlowEditorModel(configBlocks, schemaGroupBlocks);
        elements.forEach((element) => {
            if (isNode(element)) {
                element.data.collectedProperties = propsCollector(element, elements);
            }
        });
        const lastBlock = elements.find((element) => isNode(element) && isEmpty(getOutgoers(element, elements)));
        return isNil(lastBlock) ? null : getModalCollectedProperties(CJModelRelationScopes.All, lastBlock);
    }, [schemaGroupBlocks, configBlocks]);

    const getDueDateItem = (dueDate: unknown) => {
        const { type, isTypeLoading, text } = getCJVisualizationDueDateInfo(
            dueDate,
            blockNames,
            referenceProperties,
            isSchemaLoading,
            t,
        );
        const typeText = isNil(type) ? '' : `${t(CJVisualizationDueDateTypeLabels[type])}: `;
        return {
            label: l.DueDate,
            value: (
                <TittleLink
                    name={isNil(text) ? t(l.NA) : `${typeText}${text}`}
                    isClickable={false}
                    tooltipConfig={{ position: 'top' }}
                />
            ),
            isLoading: isTypeLoading,
            className: 'customer-journey-visualization-due-date',
        };
    };

    const generalDetailsData = useMemo((): KeyValueItemsProps['items'] => {
        if (isEmpty(visualizationData)) return [];

        return [
            {
                label: l.Title,
                value: (
                    <TittleLink
                        name={t(visualizationData.Name)}
                        isClickable={false}
                        tooltipConfig={{ position: 'top' }}
                    />
                ),
            },
            {
                label: l.Description,
                value: (
                    <TittleLink
                        name={t(visualizationData.Description)}
                        isClickable={false}
                        tooltipConfig={{ position: 'top' }}
                    />
                ),
            },
            {
                label: l.Languages,
                value: (
                    <div className="languages-container">
                        {visualizationData.VisualizationTemplates.map((template, index) => (
                            <Tag
                                key={index}
                                name={
                                    isNil(template.Language) ? t(l.Default) : partnerLanguages[template.Language]?.Name
                                }
                                cornerRadius="smooth-radius"
                                appearance="outline"
                            />
                        ))}
                    </div>
                ),
                isLoading: isEmpty(partnerLanguages),
            },
            {
                label: l.IsLive,
                value: BooleanTypeLabels[`${+visualizationData?.State === StateModesLabels.Live}`],
            },
            {
                label: l.VisibleTo,
                value: isNil(visualizationData.Condition) ? l.AllPlayers : l.CJPlayers,
            },
            {
                label: l.CreatedDate,
                value:
                    visualizationData.CreatedDate &&
                    customMomentWithoutTimezoneConversion(visualizationData.CreatedDate).format(partnerDateTimeFormat),
            },
            {
                label: l.User,
                value: visualizationData.CreatedBy.Name,
            },
            getDueDateItem(visualizationData.DueDate),
        ];
        // DEPS: [getDueDateItem, t, partnerDateTimeFormat]
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visualizationData, partnerLanguages, blockNames, referenceProperties, isSchemaLoading]);

    const renderIcon = (icon: Nilable<string>) => {
        const iconData = visualizationIcons?.find((item) => item.Name === icon);
        if (isNil(iconData)) {
            return null;
        }
        return <CustomerJourneyVisualizationIcon svg={iconData.Svg} />;
    };

    const getStepsInfo = useMemo(() => {
        if (isEmpty(visualizationData)) return [];

        return visualizationData.VisualizationSteps.map((step, index) => ({
            name: `${t(l.Group)} ${index + 1}`,
            info: [
                {
                    label: l.Name,
                    value: <TittleLink name={step.Name} isClickable={false} tooltipConfig={{ position: 'top' }} />,
                },
                {
                    label: l.Description,
                    value: (
                        <TittleLink
                            name={isEmpty(step.Description) ? t(l.NA) : step.Description}
                            isClickable={false}
                            tooltipConfig={{ position: 'top' }}
                        />
                    ),
                },
                {
                    label: l.Icon,
                    value: renderIcon(step.Icon),
                    isLoading: isNil(visualizationIcons),
                },
                getDueDateItem(step.DueDate),
            ],
            subSteps: step.VisualizationSubSteps.map((subStep, index) => ({
                name: `${t(l.Step)} ${index + 1}`,
                data: subStep,
                info: [
                    {
                        label: l.Name,
                        value: (
                            <TittleLink name={subStep.Name} isClickable={false} tooltipConfig={{ position: 'top' }} />
                        ),
                    },
                    {
                        label: l.Description,
                        value: (
                            <TittleLink
                                name={isEmpty(subStep.Description) ? t(l.NA) : subStep.Description}
                                isClickable={false}
                                tooltipConfig={{ position: 'top' }}
                            />
                        ),
                    },
                    {
                        label: l.IsHidden,
                        value: t(subStep.IsHidden ? l.Yes : l.No),
                    },
                    {
                        label: l.Icon,
                        value: renderIcon(subStep.Icon) ?? t(l.NA),
                        isLoading: isNil(visualizationIcons),
                    },
                    {
                        label: l.Languages,
                        value: (
                            <CustomerJourneyVisualizationLanguages
                                languages={subStep.VisualizationSubStepTemplates.map(({ Language }) => Language).filter(
                                    (language) => !isNil(language),
                                )}
                                partnerLanguages={partnerLanguages}
                            />
                        ),
                        isLoading: isEmpty(partnerLanguages),
                    },
                ],
            })),
        }));
        // DEPS: [getDueDateItem, renderIcon, t]
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visualizationData, visualizationIcons, partnerLanguages, blockNames, referenceProperties, isSchemaLoading]);

    const loadStaticData = () => {
        isEmpty(partnerLanguages) &&
            getPartnerLanguageList(dispatch, getPartnerLanguageRequest.request).catch(ignoreCanceledError);

        doGetRequest(getCustomerJourneyPreviewByIdRequest.request, {
            queryString: { workflowId },
            successCallback: ({ Config }) => {
                setConfigBlocks(JSON.parse(Config ?? JSON_OBJECT)?.Blocks ?? []);
            },
        });
    };

    const loadVersionedData = () => {
        setIsLoading(true);
        setVisualizationIcons(null);

        doGetRequest(getCustomerJourneyVisualizationByIdRequest.request, {
            queryString: { workflowId, pointInTime },
            successCallback: (Data) => {
                setVisualizationData(Data);
            },
        }).then(({ IsCanceled }) => {
            !IsCanceled && setIsLoading(false);
        });

        doGetRequest(getVisualizationIconsRequest.request, {
            queryString: { pointInTime },
            successCallback: (data) => {
                setVisualizationIcons(data ?? []);
            },
        }).then(({ IsCanceled }) => {
            !IsCanceled && setVisualizationIcons((prev) => prev ?? []);
        });
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(loadStaticData, []);

    // DEPS: [loadVersionedData]
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(loadVersionedData, [pointInTime]);

    useEffect(() => {
        if (!hasDueDateReference) {
            return;
        }
        setIsSchemaLoading(true);
        doGetRequest(getCustomerJourneySchemaRequest.request, {
            queryString: { promotionType: RealtimePromotionTypes.CustomerJourney },
            successCallback: (data) => {
                setSchemaGroupBlocks(groupBlocksBuilder(data));
            },
        }).then(() => setIsSchemaLoading(false));
        // DEPS: [doGetRequest, getCustomerJourneySchemaRequest]
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasDueDateReference]);

    return (
        <BusyLoader isBusy={isLoading} type="spinner" spinnerSize="medium">
            {!isEmpty(visualizationData) && (
                <div className="customer-journey-visualization-tab">
                    <div className="general-details-section">
                        <div className="title-wrapper">
                            <p className="title-wrapper-text">{t(l.GeneralDetails)}</p>
                            <span className="title-wrapper-line" />
                        </div>
                        <div className="general-details-content">
                            <div className="general-details-column">
                                <KeyValueItems items={generalDetailsData} />
                            </div>
                        </div>
                    </div>
                    <Tabs type="box" position="top" onChange={() => setSubTubIndex('0')}>
                        {getStepsInfo.map((step, index) => (
                            <Tab key={index} title={step.name}>
                                <>
                                    <KeyValueItems items={step.info} vertical={true} />
                                    <Tabs type="text" position="top" onChange={setSubTubIndex} activeKey={subTubIndex}>
                                        {step.subSteps.map((subStep, index) => (
                                            <Tab key={index} title={subStep.name}>
                                                <KeyValueItems items={subStep.info} vertical={true} />
                                                <CustomerJourneyVisualizationStepFeaturesInfo
                                                    subStep={subStep.data}
                                                    visualization={visualizationData}
                                                    blockNames={blockNames}

                                                    referenceProperties={referenceProperties}
                                                    isReferencePropertiesLoading={isSchemaLoading}
                                                />
                                            </Tab>
                                        ))}
                                    </Tabs>
                                </>
                            </Tab>
                        ))}
                    </Tabs>
                </div>
            )}
        </BusyLoader>
    );
};

CJVisualizationInfo.propTypes = {
    workflowId: PropTypes.number.isRequired,
};

export default memo(CJVisualizationInfo);
